import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { changeOwnPassword } from "@/api/auth-api";

const MIN_LEN = 8;

export function AccountSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const mismatch = next.length > 0 && confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < MIN_LEN;
  const sameAsCurrent =
    current.length > 0 && next.length > 0 && next === current;

  const change = useMutation({
    mutationFn: () =>
      changeOwnPassword({ current_password: current, new_password: next }),
    onSuccess: () => {
      setSuccess(true);
      setCurrent("");
      setNext("");
      setConfirm("");
      setServerError(null);
    },
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Failed to update password";
      setServerError(detail);
      setSuccess(false);
    },
  });

  const canSubmit =
    current.length > 0 &&
    next.length >= MIN_LEN &&
    confirm.length > 0 &&
    next === confirm &&
    next !== current &&
    !change.isPending;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSuccess(false);
    setServerError(null);
    change.mutate();
  };

  const eyeButton = (
    <button
      type="button"
      onClick={() => setShow((s) => !s)}
      aria-label={show ? "Hide passwords" : "Show passwords"}
      data-cy="account-password-toggle"
      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-foreground-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4"
      data-cy="settings-section-account"
    >
      <div>
        <h2 className="text-base font-semibold text-foreground-heading">
          Change password
        </h2>
        <p className="mt-1 text-xs text-foreground-muted">
          Use at least {MIN_LEN} characters. New password must differ from the current one.
        </p>
      </div>

      <label className="block text-sm">
        <span className="text-xs font-medium text-foreground-muted">Current password</span>
        <input
          data-cy="account-current-password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className="sk-input mt-1"
        />
      </label>

      <label className="block text-sm">
        <span className="text-xs font-medium text-foreground-muted">New password</span>
        <div className="relative">
          <input
            data-cy="account-new-password"
            type={show ? "text" : "password"}
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="sk-input mt-1 pr-10"
          />
          {eyeButton}
        </div>
        {tooShort ? (
          <p
            data-cy="account-new-password-error"
            className="mt-1 text-sm text-status-urgent"
          >
            Must be at least {MIN_LEN} characters.
          </p>
        ) : null}
        {sameAsCurrent ? (
          <p
            data-cy="account-new-password-same-error"
            className="mt-1 text-sm text-status-urgent"
          >
            New password must differ from current.
          </p>
        ) : null}
      </label>

      <label className="block text-sm">
        <span className="text-xs font-medium text-foreground-muted">Confirm new password</span>
        <input
          data-cy="account-confirm-password"
          type={show ? "text" : "password"}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="sk-input mt-1"
        />
        {mismatch ? (
          <p
            data-cy="account-confirm-error"
            className="mt-1 text-sm text-status-urgent"
          >
            Passwords do not match.
          </p>
        ) : null}
      </label>

      <div className="space-y-1.5">
        <button
          type="submit"
          data-cy="account-submit"
          disabled={!canSubmit}
          className="sk-btn sk-btn-primary"
        >
          {change.isPending ? "Updating…" : "Update password"}
        </button>
        {success ? (
          <p data-cy="account-success" className="text-sm text-status-done">
            Password updated
          </p>
        ) : null}
        {serverError ? (
          <p data-cy="account-server-error" className="text-sm text-status-urgent">
            {serverError}
          </p>
        ) : null}
      </div>
    </form>
  );
}
